import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColDef } from 'ag-grid-community'
import { useMemo } from 'react'

import type { GiftCard } from '@/api/admin-gift-card-types'
import { preloadListGiftCards, useListGiftCards } from '@/api/react-query'
import { Container, ContainerHeader } from '@/components/base/container'
import { IconButton } from '@/components/base/icon-button'
import { SearchField } from '@/components/base/search-field'
import type { StatusColor } from '@/components/base/status-cell'
import { SingleColumnPage } from '@/components/page-layout'
import { Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { Plus } from '@/icons/plus'
import { ConditionalRouteComponent } from '@/utils/route-utilities'

export const Route = createFileRoute('/_authenticated/_general/products/gift-cards')({
	component: () => (
		<ConditionalRouteComponent routePath={Route.id} pathsToRenderOn={['/create']}>
			<GiftCardsList />
		</ConditionalRouteComponent>
	),
	loader: async ({ context }) => await preloadListGiftCards(context, { query: {} }),
	staticData: {
		crumb: ({ context: { L } }) => L.GiftCards.Title()
	}
})

function GiftCardsList() {
	const { L } = useI18n()
	const giftCards = useListGiftCards({})

	const columnDefs = useGiftCardListColumnDefs()

	return (
		<SingleColumnPage hasOutlet>
			<Container className='flex flex-1 flex-col'>
				<ContainerHeader
					heading={L.GiftCards.Title()}
					rightSide={
						<>
							<SearchField />
							<Link from={Route.fullPath} to='create'>
								<IconButton tooltipText={L.General.Create()}>
									<Plus />
								</IconButton>
							</Link>
						</>
					}
				/>
				<Table<GiftCard>
					className='w-full flex-1'
					data={giftCards.data?.giftCards}
					columnUtilities={{}}
					columnDefs={columnDefs}
					animateRows
					rowSelection={{
						mode: 'singleRow',
						checkboxes: false,
						enableClickSelection: false,
						enableSelectionWithoutKeys: false
					}}
					suppressCellFocus
				/>
			</Container>
		</SingleColumnPage>
	)
}

export const useGiftCardListColumnDefs = () => {
	const { L } = useI18n()
	return useMemo<ColDef<GiftCard>[]>(
		() => [
			{
				headerName: L.GiftCards.Column.Code(),
				field: 'code',
				flex: 2
			},
			{
				headerName: L.GiftCards.Column.Amount(),
				field: 'amount',
				flex: 1,
				valueFormatter: ({ value }) => `$${value?.toFixed(2) || '0.00'}`
			},
			{
				headerName: L.Fields.CreatedAt(),
				field: 'createdAt',
				type: 'date',
				flex: 1
			},
			{
				headerName: L.Fields.UpdatedAt(),
				field: 'updatedAt',
				type: 'date',
				flex: 1
			}
		],
		[L]
	)
}

const getGiftCardStatusColor = (status: string): StatusColor => {
	switch (status) {
		case 'ACTIVE':
			return 'green'
		case 'RESERVED':
			return 'blue'
		case 'REDEEMED':
			return 'grey'
		default:
			return 'grey'
	}
}
